import { useState, type FormEvent } from 'react';

import { Alert, Anchor, Button, Stack, Text, TextInput, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { SessionState } from '@/shared/api/auth';
import { ApiError, ERROR_CODES } from '@/shared/api/http';
import {
  MFA_ERROR_CODES,
  MFA_STATUS,
  mfaApi,
  type MfaChallenge,
  type MfaEnrollment,
} from '@/shared/api/mfa';

import { RecoveryCodesPanel } from './RecoveryCodesPanel';
import { normalizeTotp } from './totpCode';
import { TotpEnrollment } from './TotpEnrollment';

/**
 * The step between a correct password and the session (08 §2.10, `AM-198`): a code of the
 * authenticator (or a recovery code) when the identity has a factor, or the enrolment a policy
 * demands when it has none. An expired or spent challenge sends the person back to the password.
 */
export function MfaLoginStep({
  challenge,
  onSession,
  onRestart,
}: {
  challenge: MfaChallenge;
  onSession: (state: SessionState) => void;
  onRestart: () => void;
}) {
  const { t } = useTranslation('auth');
  const [expired, setExpired] = useState(false);

  if (expired) {
    return (
      <Stack gap="md">
        <Title order={2}>{t('mfa.title')}</Title>
        <Alert color="yellow" variant="light" role="alert">
          {t('mfa.error.challenge')}
        </Alert>
        <Button onClick={onRestart}>{t('mfa.restart')}</Button>
      </Stack>
    );
  }

  return challenge.status === MFA_STATUS.required ? (
    <VerifyStep challenge={challenge} onSession={onSession} onExpired={() => setExpired(true)} />
  ) : (
    <EnrollStep challenge={challenge} onSession={onSession} onExpired={() => setExpired(true)} />
  );
}

function VerifyStep({
  challenge,
  onSession,
  onExpired,
}: {
  challenge: MfaChallenge;
  onSession: (state: SessionState) => void;
  onExpired: () => void;
}) {
  const { t } = useTranslation('auth');
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const entered = useRecovery ? value.trim() : normalizeTotp(value);
    if (useRecovery ? entered.length === 0 : !/^\d{6}$/.test(entered)) {
      setErrorKey(useRecovery ? 'mfa.error.recoveryRequired' : 'mfa.error.format');
      return;
    }
    setErrorKey(null);
    setPending(true);
    try {
      onSession(
        await mfaApi.verify(
          useRecovery
            ? { mfaToken: challenge.mfaToken, recoveryCode: entered }
            : { mfaToken: challenge.mfaToken, code: entered },
        ),
      );
    } catch (error) {
      if (error instanceof ApiError && error.errorCode === MFA_ERROR_CODES.invalidChallenge) {
        onExpired();
        return;
      }
      setErrorKey(
        error instanceof ApiError && error.errorCode === MFA_ERROR_CODES.invalidCode
          ? 'mfa.error.code'
          : error instanceof ApiError && error.errorCode === ERROR_CODES.rateLimitExceeded
            ? 'login.error.rateLimited'
            : 'mfa.error.generic',
      );
    } finally {
      setPending(false);
    }
  };

  const toggle = () => {
    setUseRecovery((current) => !current);
    setValue('');
    setErrorKey(null);
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>{t('mfa.title')}</Title>
          <Text size="sm" c="dimmed">
            {useRecovery ? t('mfa.verify.recoveryDescription') : t('mfa.verify.description')}
          </Text>
        </Stack>
        {errorKey !== null ? (
          <Alert color="red" variant="light" role="alert">
            {t(errorKey)}
          </Alert>
        ) : null}
        <TextInput
          key={useRecovery ? 'recovery' : 'code'}
          label={useRecovery ? t('mfa.recoveryCode') : t('mfa.code')}
          size="md"
          autoFocus
          {...(useRecovery
            ? { autoComplete: 'off' }
            : { inputMode: 'numeric' as const, autoComplete: 'one-time-code', maxLength: 7 })}
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
        />
        <Button type="submit" size="md" fullWidth loading={pending}>
          {t('mfa.verify.submit')}
        </Button>
        <Anchor component="button" type="button" size="sm" onClick={toggle}>
          {useRecovery ? t('mfa.verify.useApp') : t('mfa.verify.useRecovery')}
        </Anchor>
      </Stack>
    </form>
  );
}

function EnrollStep({
  challenge,
  onSession,
  onExpired,
}: {
  challenge: MfaChallenge;
  onSession: (state: SessionState) => void;
  onExpired: () => void;
}) {
  const { t } = useTranslation('auth');
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [codes, setCodes] = useState<{ codes: string[]; session: SessionState } | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const start = async () => {
    setErrorKey(null);
    setPending(true);
    try {
      setEnrollment(await mfaApi.startChallengeEnrollment(challenge.mfaToken));
    } catch (error) {
      if (error instanceof ApiError && error.errorCode === MFA_ERROR_CODES.invalidChallenge) {
        onExpired();
        return;
      }
      setErrorKey('mfa.error.generic');
    } finally {
      setPending(false);
    }
  };

  if (codes !== null) {
    return (
      <Stack gap="md">
        <Title order={2}>{t('mfa.codes.title')}</Title>
        <RecoveryCodesPanel
          codes={codes.codes}
          continueLabel={t('mfa.codes.continueToConsole')}
          onContinue={() => onSession(codes.session)}
        />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Title order={2}>{t('mfa.enroll.title')}</Title>
        <Text size="sm" c="dimmed">
          {t('mfa.enroll.required')}
        </Text>
      </Stack>
      {errorKey !== null ? (
        <Alert color="red" variant="light" role="alert">
          {t(errorKey)}
        </Alert>
      ) : null}
      {enrollment === null ? (
        <Button onClick={() => void start()} loading={pending}>
          {t('mfa.enroll.start')}
        </Button>
      ) : (
        <TotpEnrollment
          enrollment={enrollment}
          confirm={(code) =>
            mfaApi.confirmChallengeEnrollment({ mfaToken: challenge.mfaToken, code })
          }
          onConfirmed={(result) => {
            if (result.session) {
              setCodes({ codes: result.recoveryCodes, session: result.session });
            }
          }}
          onExpired={onExpired}
        />
      )}
    </Stack>
  );
}
